import { Box, FormControlLabel, RadioGroup as MuiRadioGroup, Radio, Typography, styled } from '@mui/material';
import React from 'react';
import { EColors, getColor } from 'theme';

const StyledFormControlLabel = styled(FormControlLabel)<{ disabled?: boolean }>(({ theme: { spacing }, disabled }) => ({
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
    ...(disabled && {
        opacity: 0.7,
        cursor: 'not-allowed',
        pointerEvents: 'none',
    }),
    '& .MuiRadio-root': {
        padding: 0,
        marginRight: spacing(1),
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1rem',
    },
    '& > .MuiFormControlLabel-label': {
        fontSize: '.875rem',
        color: getColor(EColors.v3TextSecondary),
        ...(disabled && {
            cursor: 'not-allowed',
        }),
    },

    '& > .MuiRadio-root.Mui-checked': {
        color: getColor(EColors.v3TextPrimary),
    },
}));

const Grid = styled(Box, {
    shouldForwardProp: prop => prop !== 'columns',
})<{ columns: number }>(({ theme: { spacing }, columns }) => ({
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gridAutoRows: 'auto',
    gap: spacing(1.25),
}));

type BaseProps = {
    options: { value: number | string | boolean; label: number | string }[];
    label?: string;
    placeholder?: string;
    error?: string | undefined;
    value?: number | string | boolean;
    disabled?: boolean | undefined;
    required?: boolean;
    onChange: (values: number | string | boolean) => void;
    onBlur?: () => void;
};

type RowProps = { row: true; columns?: never };
type ColumnProps = { row?: false; columns?: number };

type RadioGroupProps = BaseProps & (RowProps | ColumnProps);

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
    (
        { label, error = '', required = false, options, disabled, value = '', row = false, columns = 1, onChange },
        ref
    ) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange((event.target as HTMLInputElement).value);
        };

        const Options = options.map(option => {
            return (
                <StyledFormControlLabel
                    key={String(option.value)}
                    label={option.label}
                    value={option.value}
                    disabled={disabled}
                    control={<Radio />}
                />
            );
        });

        return (
            <Box>
                {label && (
                    <Box display="flex" gap={0.5} marginBottom={1}>
                        <Typography variant="body2" color={getColor(EColors.v3TextPrimary)} fontWeight={500}>
                            {label}
                        </Typography>

                        <Typography variant="body2" color={getColor(EColors.v3TextSecondary)} fontWeight={500}>
                            {required ? '*' : ''}
                        </Typography>
                    </Box>
                )}
                <MuiRadioGroup value={value} onChange={handleChange} sx={{ gap: 2 }} row={row} ref={ref}>
                    {row ? Options : <Grid columns={columns}>{Options}</Grid>}
                </MuiRadioGroup>
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error}
                    </Typography>
                )}
            </Box>
        );
    }
);
